import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { taskPhotoService } from '../../services/taskPhotoService';

import { useAuth } from '../../contexts/AuthContext';
import TaskContextBanner from './components/TaskContextBanner';
import PhotoUploadZone from './components/PhotoUploadZone';
import UploadedPhotoThumb from './components/UploadedPhotoThumb';

const PhotoUploadPrompt = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const taskId = searchParams?.get('taskId');
  const siteId = searchParams?.get('siteId');
  const returnTo = searchParams?.get('returnTo') || '/task-completion-workflow';

  const [task, setTask] = useState(null);
  const [loadingTask, setLoadingTask] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [allUploaded, setAllUploaded] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (taskId) loadTask();
    else setLoadingTask(false);
  }, [taskId]);

  const loadTask = async () => {
    setLoadingTask(true);
    try {
      // Fetch a single task by id via a targeted query
      const { supabase } = await import('../../lib/supabase');
      const { data, error: err } = await supabase
        ?.from('daily_tasks')
        ?.select('*, site:sites!daily_tasks_site_id_fkey(id, site_name)')
        ?.eq('id', taskId)
        ?.single();
      if (!err && data) {
        setTask({
          id: data?.id,
          title: data?.title,
          category: data?.category,
          required: data?.required,
          photo_required: data?.photo_required,
          site: data?.site,
        });
      }
    } catch (e) {
      console.error('Failed to load task:', e);
    } finally {
      setLoadingTask(false);
    }
  };

  const handleFilesSelected = useCallback((files) => {
    const newPhotos = files?.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      status: 'pending',
      progress: 0,
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
    setAllUploaded(false);
  }, []);

  const handleRemovePhoto = useCallback((photoId) => {
    setPhotos(prev => {
      const photo = prev?.find(p => p?.id === photoId);
      if (photo?.preview) URL.revokeObjectURL(photo?.preview);
      return prev?.filter(p => p?.id !== photoId);
    });
  }, []);

  const handleUploadAll = async () => {
    if (!taskId || !siteId) {
      setError('Missing task or site information.');
      return;
    }
    const pending = photos?.filter(p => p?.status === 'pending');
    if (!pending?.length) return;

    setUploading(true);
    setError('');
    let successCount = 0;

    let lastError = null;
    for (const photo of pending) {
      setPhotos(prev => prev?.map(p => p?.id === photo?.id ? { ...p, status: 'uploading', progress: 10 } : p));
      const { data, error: uploadErr } = await taskPhotoService?.uploadPhoto(photo?.file, taskId, siteId);
      if (uploadErr) {
        lastError = uploadErr;
        setPhotos(prev => prev?.map(p => p?.id === photo?.id ? { ...p, status: 'error' } : p));
      } else {
        setPhotos(prev => prev?.map(p => p?.id === photo?.id ? { ...p, status: 'done', progress: 100, dbId: data?.id } : p));
        successCount++;
      }
    }

    setUploading(false);
    if (successCount > 0) setAllUploaded(true);
    if (successCount < pending?.length) {
      const failedCount = pending?.length - successCount;
      const reason = lastError?.code === 'PGRST301' || lastError?.code === '42501'
        ? `permission denied — ${lastError?.message}`
        : lastError?.message || 'unknown error';
      setError(`${failedCount} photo(s) failed to upload (${reason}). Please retry.`);
    }
  };

  const handleComplete = () => {
    navigate(returnTo);
  };

  const handleSkip = () => {
    navigate(returnTo);
  };

  const pendingCount = photos?.filter(p => p?.status === 'pending')?.length;
  const uploadedCount = photos?.filter(p => p?.status === 'done')?.length;
  const hasPhotos = photos?.length > 0;
  const canComplete = uploadedCount > 0 || !task?.photo_required;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Upload Task Photos | SiteProof</title>
      </Helmet>
      <Header />
      <main className="max-w-lg mx-auto px-4 py-6 pb-24">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleSkip}
            className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
          >
            <Icon name="ArrowLeft" size={18} className="text-foreground" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground">Upload Photos</h1>
            <p className="text-xs text-muted-foreground">Document your completed work</p>
          </div>
        </div>

        {/* Task context */}
        {loadingTask ? (
          <div className="bg-card border border-border rounded-2xl p-4 animate-pulse">
            <div className="h-4 bg-muted rounded w-1/3 mb-2" />
            <div className="h-5 bg-muted rounded w-2/3" />
          </div>
        ) : task ? (
          <TaskContextBanner task={task} />
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
            <Icon name="Info" size={18} className="text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-700">Upload photos to document your completed task.</p>
          </div>
        )}

        {/* Upload zone */}
        <div className="mt-5">
          <PhotoUploadZone onFilesSelected={handleFilesSelected} uploading={uploading} />
        </div>

        {/* Photo grid */}
        {hasPhotos && (
          <div className="mt-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-foreground">
                {photos?.length} photo{photos?.length !== 1 ? 's' : ''} selected
              </h2>
              {uploadedCount > 0 && (
                <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                  <Icon name="CheckCircle2" size={13} />{uploadedCount} uploaded
                </span>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {photos?.map(photo => (
                <UploadedPhotoThumb
                  key={photo?.id}
                  photo={photo}
                  onRemove={handleRemovePhoto}
                />
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
            <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Upload progress summary */}
        {uploading && (
          <div className="mt-4 flex items-center gap-3 p-3 bg-accent/5 border border-accent/20 rounded-xl">
            <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin flex-shrink-0" />
            <p className="text-sm text-accent font-medium">Uploading photos to secure storage...</p>
          </div>
        )}

        {allUploaded && !uploading && (
          <div className="mt-4 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
            <Icon name="CheckCircle2" size={16} className="text-emerald-600 flex-shrink-0" />
            <p className="text-sm text-emerald-700 font-medium">All photos uploaded successfully!</p>
          </div>
        )}
      </main>

      {/* Sticky bottom actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur border-t border-border px-4 py-4 safe-area-inset-bottom">
        <div className="max-w-lg mx-auto flex gap-3">
          {/* Skip / Complete */}
          {!canComplete ? (
            <button
              onClick={handleSkip}
              className="flex-1 py-3 rounded-xl border border-border text-muted-foreground text-sm font-semibold hover:bg-muted/50 transition-colors"
            >
              Skip Photos
            </button>
          ) : null}

          {/* Upload button */}
          {pendingCount > 0 && !uploading && (
            <button
              onClick={handleUploadAll}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-accent text-white rounded-xl text-sm font-semibold hover:bg-accent/90 transition-colors"
            >
              <Icon name="Upload" size={16} />
              Upload {pendingCount} Photo{pendingCount !== 1 ? 's' : ''}
            </button>
          )}

          {/* Complete task button */}
          {canComplete && pendingCount === 0 && (
            <button
              onClick={handleComplete}
              disabled={uploading}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-500 text-white rounded-xl text-sm font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-60"
            >
              <Icon name="CheckCircle2" size={16} />
              {uploadedCount > 0 ? 'Done — Task Complete' : 'Complete Task'}
            </button>
          )}

          {!hasPhotos && (
            <button
              onClick={handleSkip}
              className="flex-1 py-3 rounded-xl border border-border text-muted-foreground text-sm font-semibold hover:bg-muted/50 transition-colors"
            >
              Skip Photos
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadPrompt;
